<template>
  <!-- „Für Sie heute“: deterministische Hinweise aus dem Benutzerkontext
       (überfällige Wiedervorlagen, Zustellprobleme, Neuerungen …). Jeder
       Hinweis ist ausblendbar; das merkt sich der Server für 7 Tage. -->
  <section v-if="insights.length > 0" :aria-label="$gt('Für Sie heute')" class="ai-insights">
    <h3 class="ai-insights__title">
      <Lightbulb aria-hidden="true" :size="14" />
      {{ $gt('Für Sie heute') }}
    </h3>
    <TransitionGroup class="ai-insights__list" name="ai-card" tag="ul">
      <li
        v-for="(insight, index) in insights"
        :key="insight.key"
        class="ai-card"
        :class="`is-${insight.severity}`"
        :style="{ '--i': index }"
      >
        <span aria-hidden="true" class="ai-card__icon">
          <component :is="iconFor(insight)" :size="16" />
        </span>
        <div class="ai-card__body">
          <strong class="ai-card__title">{{ insight.title }}</strong>
          <p class="ai-card__text">{{ insight.text }}</p>
          <div class="ai-card__actions">
            <button v-if="insight.route" class="ai-link" type="button" @click="$emit('open', insight.route)">
              {{ $gt('Öffnen') }}
              <ArrowUpRight aria-hidden="true" class="ai-link__icon" :size="13" />
            </button>
            <button class="ai-card__ask" type="button" @click="$emit('ask', insight)">
              {{ $gt('Mehr dazu') }}
            </button>
          </div>
        </div>
        <button
          :aria-label="$gt('Hinweis ausblenden')"
          class="ai-icon-btn ai-icon-btn--dark ai-card__dismiss"
          :title="$gt('Für 7 Tage ausblenden')"
          type="button"
          @click="$emit('dismiss', insight.key)"
        >
          <X :size="14" />
        </button>
      </li>
    </TransitionGroup>
  </section>
</template>

<script setup lang="ts">
  import type { AssistantInsight, AssistantRoute } from '@/services/assistantApi'
  import {
    AlertTriangle,
    ArrowUpRight,
    BellRing,
    CalendarClock,
    Info,
    Lightbulb,
    MailWarning,
    PartyPopper,
    Sparkles,
    X,
  } from 'lucide-vue-next'

  defineProps<{ insights: AssistantInsight[] }>()

  defineEmits<{
    (e: 'open', route: AssistantRoute): void
    (e: 'dismiss', key: string): void
    (e: 'ask', insight: AssistantInsight): void
  }>()

  const ICONS: Record<string, unknown> = {
    'followups-overdue': CalendarClock,
    'followups-today': CalendarClock,
    'unread-messages': BellRing,
    'delivery-problems': MailWarning,
    'first-login': PartyPopper,
    'whats-new': Sparkles,
    'aipages-low': AlertTriangle,
  }

  function iconFor (insight: AssistantInsight) {
    if (insight.key in ICONS) return ICONS[insight.key]
    if (insight.severity === 'warn') return AlertTriangle
    return Info
  }
</script>

<style scoped>

  .ai-insights {
    display: grid;
    gap: 8px;
  }

  .ai-insights__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: var(--ai-muted);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .ai-insights__list {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .ai-card {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 10px;
    padding: 12px 10px 12px 12px;
    border-radius: 16px;
    border: 1px solid var(--ai-line);
    background: #fff;
    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.05);
    animation: ai-rise 420ms var(--ai-ease) both;
    animation-delay: calc(var(--i, 0) * 70ms);
  }

  .ai-card::before {
    content: '';
    position: absolute;
    inset: 10px auto 10px 0;
    width: 3px;
    border-radius: 999px;
    background: var(--ai-info);
  }

  .ai-card.is-warn::before { background: var(--ai-warn); }
  .ai-card.is-hint::before { background: var(--ai-hint); }

  .ai-card__icon {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: var(--ai-info-soft);
    color: var(--ai-info);
  }

  .ai-card.is-warn .ai-card__icon { background: var(--ai-warn-soft); color: var(--ai-warn); }
  .ai-card.is-hint .ai-card__icon { background: var(--ai-hint-soft); color: var(--ai-hint); }

  .ai-card__body {
    min-width: 0;
  }

  .ai-card__title {
    display: block;
    font-size: 13.5px;
    line-height: 1.3;
    color: var(--ai-ink);
  }

  .ai-card__text {
    margin: 3px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: var(--ai-muted);
  }

  .ai-card__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  .ai-card__ask {
    padding: 4px 8px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--ai-muted);
    font: inherit;
    font-size: 13px;
    cursor: pointer;
  }

  .ai-card__ask:hover {
    color: var(--ai-indigo);
    background: var(--ai-surface-2);
  }

  .ai-card__dismiss {
    width: 28px;
    height: 28px;
    color: var(--ai-muted);
  }

  .ai-card-leave-active {
    transition: opacity 200ms, transform 240ms var(--ai-ease);
  }

  .ai-card-leave-to {
    opacity: 0;
    transform: translateX(16px);
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-card {
      animation: none;
    }
  }
</style>
